// src/components/ConsentGate.jsx
// Blocking legal-consent screen shown once, before a new user (or any user whose
// accepted version is stale) can enter the app. Rendered by AuthHandler in place
// of <Routes> when useOrganization().needsConsent is true. Covers both auth paths
// (email code + Google) since it gates at the context layer, not per handler.
import React, { useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useOrganization } from '../context/OrganizationContext';
import { LEGAL_URLS } from '../config/legal';

export default function ConsentGate() {
  const { acceptConsent } = useOrganization();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await acceptConsent();
      // needsConsent flips to false in context → AuthHandler renders the app.
    } catch (e) {
      console.error('Error al guardar la aceptación de términos:', e);
      setError('No se pudo guardar tu aceptación. Intenta nuevamente.');
      setSubmitting(false);
    }
  };

  const link = (href, text) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={styles.link}>{text}</a>
  );

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <h2 style={styles.title}>Antes de continuar</h2>
        <p style={styles.text}>
          Para usar PUX necesitas leer y aceptar nuestros documentos legales.
        </p>

        <label style={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            disabled={submitting}
            style={styles.checkbox}
          />
          <span style={styles.checkboxText}>
            He leído y acepto los {link(LEGAL_URLS.terms, 'Términos y Condiciones')}, la{' '}
            {link(LEGAL_URLS.privacy, 'Política de Privacidad')} y la{' '}
            {link(LEGAL_URLS.aiTransparency, 'Transparencia de IA')} de PUX.
          </span>
        </label>

        {error && <p style={styles.error}>{error}</p>}

        <button
          type="button"
          onClick={handleAccept}
          disabled={!checked || submitting}
          style={{
            ...styles.button,
            backgroundColor: (!checked || submitting) ? '#c7c7d1' : '#667eea',
            cursor: (!checked || submitting) ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? 'Guardando…' : 'Aceptar y continuar'}
        </button>

        <button
          type="button"
          onClick={() => signOut(auth)}
          disabled={submitting}
          style={styles.signout}
        >
          Cerrar sesión
        </button>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    background: '#fff',
    borderRadius: 14,
    padding: '32px 28px',
    boxShadow: '0 10px 40px rgba(0,0,0,.18)',
    boxSizing: 'border-box',
  },
  title: { margin: '0 0 12px', color: '#111827', fontSize: 22, fontWeight: 700, textAlign: 'center' },
  text: { margin: '0 0 20px', color: '#4B5563', fontSize: 15, lineHeight: 1.6, textAlign: 'center' },
  checkboxRow: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20 },
  checkbox: { marginTop: 3, width: 18, height: 18, flexShrink: 0, cursor: 'pointer' },
  checkboxText: { fontSize: 14, color: '#374151', lineHeight: 1.6 },
  link: { color: '#4F46E5', fontWeight: 600, textDecoration: 'none' },
  error: { color: '#b91c1c', fontSize: 13, margin: '0 0 12px', textAlign: 'center' },
  button: {
    width: '100%', padding: '12px', color: '#fff', border: 'none',
    borderRadius: 8, fontSize: 16, fontWeight: 600, transition: 'background-color .15s',
  },
  signout: {
    width: '100%', marginTop: 10, padding: '8px', background: 'transparent',
    color: '#6b7280', border: 'none', fontSize: 13, cursor: 'pointer', textDecoration: 'underline',
  },
};
